/**
 * The live feed seam (TT-1110): one interface the live views and hooks read
 * a meeting through, with two implementations.
 *
 *   - the OWNER client — the host's page, addressed by (userId, docId) over
 *     the Cognito-gated routes; it may also nudge the live cassette's planner
 *     (the board advance) and ask for a new cassette;
 *   - the LINK client — a public viewer, addressed by a live-link token over
 *     the /live/{token} routes; read-only by construction: the advance and
 *     request members are absent, and the hooks skip what the client cannot do.
 *
 * Both read the spoken doc model (TT-1270): `getDoc()` is the doc read (what
 * the doc has: its source, its cassettes and their states), `getBands(csstId,
 * params)` one cassette's bands, a cold page or a `since` delta.
 *
 * Both return appback's response objects as the service does (an `{ error,
 * status }` object on failure rather than a throw). A client is a pure
 * function of its target: callers memoize it on that target, and the hooks
 * key their polls on the client's identity.
 */

import AppBackService from "../services/appback";

export function createOwnerFeedClient(userId, docId) {
  if (!userId || !docId) return null;
  return {
    getDoc: () => AppBackService.getSpokenDoc(userId, docId),
    getBands: (csstId, params) => AppBackService.getDocBands(userId, docId, csstId, params),
    getSceneBoard: (since) => AppBackService.getSceneBoard(userId, docId, since),
    advanceSceneBoard: (archetype) => AppBackService.advanceSceneBoard(userId, docId, { archetype }),
    requestCassette: (request) => AppBackService.requestCassette(userId, docId, request),
  };
}

export function createLinkFeedClient(token) {
  if (!token) return null;
  return {
    getDoc: () => AppBackService.getLiveLinkDoc(token),
    getBands: (csstId, params) => AppBackService.getLiveLinkBands(token, csstId, params),
    getSceneBoard: (since) => AppBackService.getLiveLinkSceneBoard(token, since),
    advanceSceneBoard: null,
    requestCassette: null,
  };
}

// Whether the cassette's header names a scene planner (`scenegen`) among its
// algorithms: it then plans at its own pace, and an advance is the server's
// no-op.
export function plansItself(cassette) {
  return (cassette?.algorithms || []).some((a) => a?.role === "planner" && a?.name === "scenegen");
}

// The feed the board views poll through. A live cassette that plans itself
// (`plansItself` of it) needs no advance, so the advance stands down; until
// the platform's planner policy names one, it is the nudge that keeps the
// board moving. `selfPlanning` is asked at each advance, not at wrap time,
// so the wrapped feed keeps one identity across doc re-reads: a new feed
// restarts the board poll and drops the series it holds.
export function boardFeed(feed, selfPlanning) {
  if (!feed?.advanceSceneBoard) return feed;
  return { ...feed, advanceSceneBoard: (...args) => (selfPlanning() ? null : feed.advanceSceneBoard(...args)) };
}

// A revoked or expired link answers 410 on every read; the viewer page turns
// that into its "this link has ended" state instead of retrying forever.
export function isLinkGone(response) {
  return response?.status === 410;
}
